import { useImgproxy } from "@citychallenge/core";
import { observer } from "mobx-react";
import { FC, useEffect, useState } from "react";

import { useDivisionInfo } from "../../lib/use-division-info";
import styles from "./pages-loader.module.scss";

export interface PagesLoaderProps {
  /** Fills its container instead of the viewport — for in-page regions. */
  inline?: boolean;
  /** Covers the viewport, for the boot loader before the shell exists. */
  fullScreen?: boolean;
}

/**
 * Loading state of the pages runtime: the project's own mark over its brandkit
 * background, with an indeterminate bar beneath it.
 *
 * The mark is rendered as a contained `img` rather than through the shared
 * `Image` component, which crops to `background-size: cover` — fine for
 * photography, wrong for a logo, since it clips wordmarks and any padding the
 * file carries.
 *
 * Colours come from the brandkit custom properties the division theme sets, so
 * this follows the active project. The division falls back to the
 * server-injected config, which is all that exists on a cold load.
 */
export const PagesLoader: FC<PagesLoaderProps> = observer(
  ({ inline, fullScreen }) => {
    const { name, icon } = useDivisionInfo();
    // Generous width: the mark is capped by CSS, so this only sets the pixel
    // density it's decoded at.
    const iconUrl = useImgproxy(icon, { width: 480, quality: 90 });
    // A missing or broken logo falls back to the monogram rather than leaving a
    // broken image on the one screen that is purely brand.
    const [iconFailed, setIconFailed] = useState(false);

    useEffect(() => setIconFailed(false), [iconUrl]);

    return (
      // A section, not a div: the shell stretches `> section` to fill the
      // column, which is how the loader covers the space the page will occupy.
      <section
        className={
          fullScreen
            ? styles.fullScreen
            : inline
            ? styles.inline
            : styles.screen
        }
        role="status"
        aria-label={name ? `Loading ${name}` : "Loading"}
      >
        <div className={styles.mark}>
          {iconUrl && !iconFailed ? (
            <img
              className={styles.logo}
              src={iconUrl}
              alt=""
              onError={() => setIconFailed(true)}
            />
          ) : (
            <span className={styles.monogram}>{name?.[0]?.toUpperCase()}</span>
          )}

          <span className={styles.track} aria-hidden>
            <span className={styles.bar} />
          </span>
        </div>
      </section>
    );
  }
);
